<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue"
import { AtSign, Bell, BellOff, ChevronLeft, ChevronRight, Folder, Info, ListTree, Plus, Shield, Trash2 } from "lucide-vue-next"
import { toast } from "vue-sonner"
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar"
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "~/components/ui/drawer"
import { ScrollArea } from "~/components/ui/scroll-area"
import { Switch } from "~/components/ui/switch"
import NotificationTargetPanel, { type NotificationPanelSection } from "~/components/voice-ui/Notifications/NotificationTargetPanel.vue"
import { useMobileDrawerDepthBackNav } from "~/composables/useMobileDrawerBackNav"
import { useNotificationOverrides, type NotificationTargetInfo } from "~/composables/useNotificationOverrides"
import { useNotificationSettingsDialog } from "~/composables/useNotificationSettingsDialog"
import { useSwipeBack } from "~/composables/useSwipeBack"
import { resolveMediaUrl } from "~/lib/mediaCache"
import {
  formatMutedUntil,
  inheritedLevelLabel,
  LEVEL_LABELS,
  NotificationLevel,
  NotificationTargetType,
  type ResolvedNotification,
} from "~/lib/notifications/notificationPolicy"
import { useGroupsStore } from "~/stores/groups"
import { useNotificationSettingsStore } from "~/stores/notificationSettings"

// Mobile «Настройки уведомлений» of a group, laid out like the profile Drawer (MobileProfileDrawer.vue):
// a short root list — the group's mute and level, the two suppression switches, «Каналы и категории» —
// whose rows slide their choices in from the right. Pages stack (a channel's level, then its mute):
// only the first one slides over the root list, deeper ones swap in place; the back arrow, a swipe
// right or the Android back button pop one.
const { isOpen, groupId } = useNotificationSettingsDialog()
const store = useNotificationSettingsStore()
const groupsStore = useGroupsStore()

const gid = computed(() => groupId.value ?? "")
const groupDetails = computed(() => groupsStore.groupDetails[gid.value])
const groupName = computed(() => groupDetails.value?.name ?? groupsStore.groups.find((g) => g.id === gid.value)?.name ?? "")
const groupAvatarUrl = computed(() => resolveMediaUrl(groupDetails.value?.avatar_url, "avatars"))
const groupInitials = computed(() => groupName.value.trim().substring(0, 2).toUpperCase() || "??")
const settings = computed(() => store.settingsFor(gid.value))
const group = computed(() => store.resolveGroup(gid.value))

const { overrideRows, addableCategories, addTarget, targetInfo, targetIcon, removeRow, resetAdded } = useNotificationOverrides(gid)

// ── Pages ────────────────────────────────────────────────────────────────────────────────────────

type Page =
  | { kind: "group", section: NotificationPanelSection }
  | { kind: "overrides" }
  | { kind: "add" }
  | { kind: "target", info: NotificationTargetInfo, section: NotificationPanelSection }

const stack = ref<Page[]>([])
// The page still drawn in the sliding pane while it slides back out to the root list.
const shownPage = ref<Page | null>(null)
const SLIDE_DURATION_MS = 300
let clearPageTimer: ReturnType<typeof setTimeout> | null = null

watch(stack, (pages) => {
  if (clearPageTimer) clearTimeout(clearPageTimer)
  clearPageTimer = null
  const top = pages.at(-1)
  if (top) {
    shownPage.value = top
    return
  }
  clearPageTimer = setTimeout(() => {
    clearPageTimer = null
    if (!stack.value.length) shownPage.value = null
  }, SLIDE_DURATION_MS)
}, { deep: true })
onBeforeUnmount(() => {
  if (clearPageTimer) clearTimeout(clearPageTimer)
})

function push(page: Page) {
  stack.value = [...stack.value, page]
}
function replaceTop(page: Page) {
  stack.value = [...stack.value.slice(0, -1), page]
}
function pop() {
  stack.value = stack.value.slice(0, -1)
}

watch(isOpen, (open) => {
  if (open) {
    stack.value = []
    shownPage.value = null
  } else {
    resetAdded()
  }
})

const navDepth = computed({
  get: () => stack.value.length,
  set: (depth: number) => {
    if (depth < stack.value.length) stack.value = stack.value.slice(0, Math.max(0, depth))
  },
})
useMobileDrawerDepthBackNav({ isOpen, depth: navDepth, enabled: ref(true) })

const stripRef = ref<HTMLElement | null>(null)
const { stripStyle, handlers: swipeBackHandlers } = useSwipeBack({
  stripRef,
  isOnSecondPane: () => stack.value.length > 0,
  onBack: pop,
})

const MUTE_TITLES = { category: "Заглушить категорию", channel: "Заглушить канал" } as const
const pageTitle = computed(() => {
  const page = shownPage.value
  if (!page) return ""
  if (page.kind === "group") return page.section === "mute" ? "Заглушить группу" : "Уведомления группы"
  if (page.kind === "overrides") return "Каналы и категории"
  if (page.kind === "add") return "Добавить"
  if (page.section === "mute") return page.info.type === NotificationTargetType.Category ? MUTE_TITLES.category : MUTE_TITLES.channel
  return page.info.type === NotificationTargetType.Category ? page.info.name : `#${page.info.name}`
})

// ── Root rows ────────────────────────────────────────────────────────────────────────────────────

async function run(action: () => Promise<unknown>) {
  try {
    await action()
  } catch {
    toast.error("Не удалось изменить настройки уведомлений")
  }
}

// Muted, the row turns into «Включить оповещения группы» and unmutes right away.
function onGroupMuteRow() {
  if (group.value.ownMuted) void run(() => store.unmute(gid.value, NotificationTargetType.Group, gid.value))
  else push({ kind: "group", section: "mute" })
}

function setSuppress(key: "suppress_everyone" | "suppress_roles", value: boolean) {
  void run(() => store.setSuppress(gid.value, { [key]: value }))
}

function rowSummary(resolved: ResolvedNotification): string {
  if (resolved.ownMuted) return formatMutedUntil(resolved.ownMutedUntil, store.now)
  if (resolved.ownLevel === NotificationLevel.Inherit) return inheritedLevelLabel(resolved)
  return LEVEL_LABELS[resolved.ownLevel]
}

function pickTarget(id: string) {
  const info = targetInfo(id)
  if (!info) return
  addTarget(id)
  replaceTop({ kind: "target", info, section: "level" })
}
</script>

<template>
  <Drawer v-model:open="isOpen">
    <DrawerContent class="flex max-h-[94vh] flex-col gap-0 p-0" data-testid="notification-settings-drawer">
      <div class="relative min-h-0 flex-1 overflow-hidden">
        <!-- `touch-pan-y` on the strip and its scroll areas: see MobileSettingsPage.vue. -->
        <div
            ref="stripRef"
            class="flex h-full w-[200%] touch-pan-y transition-transform duration-300 ease-out [&_[data-slot=scroll-area-viewport]]:touch-pan-y"
            :style="stripStyle"
            v-on="swipeBackHandlers"
        >
          <!-- Root pane -->
          <div class="flex h-full w-1/2 min-h-0 flex-col overflow-y-auto overscroll-contain" data-testid="notification-settings-root-pane">
            <DrawerHeader class="items-center text-center">
              <Avatar class="size-16">
                <AvatarImage v-if="groupDetails?.avatar_url" :src="groupAvatarUrl"/>
                <AvatarFallback>{{ groupInitials }}</AvatarFallback>
              </Avatar>
              <DrawerTitle>{{ groupName }}</DrawerTitle>
              <DrawerDescription>Настройки уведомлений</DrawerDescription>
            </DrawerHeader>

            <div class="shrink-0 space-y-3 pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)]">
              <p v-if="settings?.large_group" class="mx-4 flex gap-2 rounded-2xl border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
                <Info class="mt-px size-4 shrink-0"/>
                В группе больше 1000 участников — Push приходят только на упоминания
              </p>

              <div class="mx-4 divide-y divide-border/60 overflow-hidden rounded-2xl border bg-muted/40">
                <button
                    type="button"
                    class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted"
                    @click="onGroupMuteRow"
                >
                  <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <Bell v-if="group.ownMuted" class="size-5"/>
                    <BellOff v-else class="size-5"/>
                  </span>
                  <span class="min-w-0 flex-1 truncate text-[15px] font-medium">
                    {{ group.ownMuted ? 'Включить оповещения группы' : 'Заглушить группу' }}
                  </span>
                  <ChevronRight v-if="!group.ownMuted" class="size-5 shrink-0 text-muted-foreground/70"/>
                </button>
                <button
                    type="button"
                    class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted"
                    @click="push({ kind: 'group', section: 'level' })"
                >
                  <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <Bell class="size-5"/>
                  </span>
                  <span class="min-w-0 flex-1 truncate text-[15px] font-medium">Уведомления</span>
                  <ChevronRight class="size-5 shrink-0 text-muted-foreground/70"/>
                </button>
              </div>

              <div class="mx-4 divide-y divide-border/60 overflow-hidden rounded-2xl border bg-muted/40">
                <label class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5">
                  <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <AtSign class="size-5"/>
                  </span>
                  <span class="min-w-0 flex-1 text-[15px] font-medium">Подавлять @everyone и @here</span>
                  <Switch
                      :model-value="!!settings?.suppress_everyone"
                      @update:model-value="setSuppress('suppress_everyone', !!$event)"
                  />
                </label>
                <label class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5">
                  <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <Shield class="size-5"/>
                  </span>
                  <span class="min-w-0 flex-1 text-[15px] font-medium">Подавлять упоминания ролей</span>
                  <Switch
                      :model-value="!!settings?.suppress_roles"
                      @update:model-value="setSuppress('suppress_roles', !!$event)"
                  />
                </label>
              </div>

              <div class="mx-4 overflow-hidden rounded-2xl border bg-muted/40">
                <button
                    type="button"
                    class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted"
                    @click="push({ kind: 'overrides' })"
                >
                  <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <ListTree class="size-5"/>
                  </span>
                  <span class="min-w-0 flex-1 truncate text-[15px] font-medium">Каналы и категории</span>
                  <span v-if="overrideRows.length" class="shrink-0 text-sm text-muted-foreground tabular-nums">{{ overrideRows.length }}</span>
                  <ChevronRight class="size-5 shrink-0 text-muted-foreground/70"/>
                </button>
              </div>
            </div>
          </div>

          <!-- Page pane -->
          <div class="flex h-full w-1/2 min-h-0 flex-col">
            <template v-if="shownPage">
              <div class="relative flex shrink-0 items-center justify-center px-14 pb-2 pt-3">
                <button
                    type="button"
                    aria-label="Назад"
                    class="absolute left-3 flex size-10 items-center justify-center rounded-full bg-muted/70 text-muted-foreground transition-transform duration-150 active:scale-90"
                    @click="pop"
                >
                  <ChevronLeft class="size-5"/>
                </button>
                <h2 class="truncate text-lg font-semibold">{{ pageTitle }}</h2>
              </div>

              <ScrollArea class="min-h-0 flex-1">
                <div class="pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)] pt-3">
                  <NotificationTargetPanel
                      v-if="shownPage.kind === 'group'"
                      :group-id="gid"
                      target="group"
                      :section="shownPage.section"
                      @open="push({ kind: 'group', section: $event })"
                      @back="pop"
                  />

                  <NotificationTargetPanel
                      v-else-if="shownPage.kind === 'target'"
                      :key="shownPage.info.id + shownPage.section"
                      :group-id="gid"
                      :target="shownPage.info.type === NotificationTargetType.Category ? 'category' : 'channel'"
                      :target-id="shownPage.info.id"
                      :category-id="shownPage.info.categoryId"
                      :section="shownPage.section"
                      @open="push({ kind: 'target', info: shownPage.info, section: $event })"
                      @back="pop"
                  />

                  <div v-else-if="shownPage.kind === 'overrides'" class="space-y-3">
                    <div v-if="overrideRows.length" class="mx-4 divide-y divide-border/60 overflow-hidden rounded-2xl border bg-muted/40">
                      <div v-for="row in overrideRows" :key="row.info.id" class="flex min-h-12 items-center">
                        <button
                            type="button"
                            class="flex min-w-0 flex-1 items-center gap-3 py-2.5 pl-4 text-left transition-colors active:bg-muted"
                            @click="push({ kind: 'target', info: row.info, section: 'level' })"
                        >
                          <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                            <component :is="targetIcon(row.info)" class="size-5"/>
                          </span>
                          <span class="grid min-w-0 flex-1 leading-tight">
                            <span class="truncate text-[15px] font-medium">{{ row.info.name }}</span>
                            <span class="truncate text-xs text-muted-foreground">{{ rowSummary(row.resolved) }}</span>
                          </span>
                        </button>
                        <button
                            type="button"
                            aria-label="Сбросить настройки"
                            class="flex size-12 shrink-0 items-center justify-center text-muted-foreground active:text-foreground"
                            @click="removeRow(row.info, row.resolved)"
                        >
                          <Trash2 class="size-5"/>
                        </button>
                      </div>
                    </div>
                    <p v-else class="px-4 text-sm text-muted-foreground">Все каналы и категории следуют настройкам группы.</p>

                    <div v-if="addableCategories.length" class="mx-4 overflow-hidden rounded-2xl border bg-muted/40">
                      <button
                          type="button"
                          class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted"
                          @click="push({ kind: 'add' })"
                      >
                        <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
                          <Plus class="size-5"/>
                        </span>
                        <span class="min-w-0 flex-1 truncate text-[15px] font-medium">Добавить канал или категорию</span>
                        <ChevronRight class="size-5 shrink-0 text-muted-foreground/70"/>
                      </button>
                    </div>
                  </div>

                  <div v-else-if="shownPage.kind === 'add'" class="space-y-4">
                    <section v-for="entry in addableCategories" :key="entry.category.id" class="space-y-2">
                      <h3 class="truncate px-4 text-xs font-semibold uppercase text-muted-foreground">{{ entry.category.name }}</h3>
                      <div class="mx-4 divide-y divide-border/60 overflow-hidden rounded-2xl border bg-muted/40">
                        <button
                            v-if="!entry.categoryTaken"
                            type="button"
                            class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted"
                            @click="pickTarget(entry.category.id)"
                        >
                          <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                            <Folder class="size-5"/>
                          </span>
                          <span class="min-w-0 flex-1 truncate text-[15px] font-medium">Вся категория</span>
                        </button>
                        <button
                            v-for="channel in entry.channels"
                            :key="channel.id"
                            type="button"
                            class="flex min-h-12 w-full items-center gap-3 px-4 py-2.5 text-left transition-colors active:bg-muted"
                            @click="pickTarget(channel.id)"
                        >
                          <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                            <component :is="targetInfo(channel.id) ? targetIcon(targetInfo(channel.id)!) : Folder" class="size-5"/>
                          </span>
                          <span class="min-w-0 flex-1 truncate text-[15px] font-medium">{{ channel.name }}</span>
                        </button>
                      </div>
                    </section>
                  </div>
                </div>
              </ScrollArea>
            </template>
          </div>
        </div>
      </div>
    </DrawerContent>
  </Drawer>
</template>
